import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { type MemoryItem, memoryApi } from '../api/memory'
import { getProjects } from '../api/projects'
import { MemoryComposer } from '../components/memory/MemoryComposer'
import { MemoryList } from '../components/memory/MemoryList'
import type { ProjectResponse } from '../types/api'

export function MemoryPage() {
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [selectedProject, setSelectedProject] = useState('')
  const [memories, setMemories] = useState<MemoryItem[]>([])
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects()
        setProjects(data)
        if (data.length > 0) {
          setSelectedProject((prev) => prev || data[0].name)
        }
      } catch {
        toast.error('Failed to load projects')
      }
    }
    fetchProjects()
  }, [])

  const refresh = useCallback(async () => {
    if (!selectedProject) {
      setMemories([])
      return
    }
    setBusy(true)
    setError(null)
    try {
      setMemories(
        query.trim()
          ? await memoryApi.search({ projectName: selectedProject, query: query.trim() })
          : await memoryApi.list(selectedProject)
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }, [selectedProject, query])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-harbor-text">Memory</h1>
        <p className="text-harbor-text-secondary mt-1">
          Decisions, preferences and open questions learned from conversations — separate from the
          documents your project has ingested.
        </p>
      </div>

      <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.name}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Search
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Search memories…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search memories"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <MemoryComposer
          disabled={busy || !selectedProject}
          onSubmit={async (text) => {
            await memoryApi.add(selectedProject, [{ role: 'user', content: text }])
            await refresh()
          }}
        />
      </div>

      <MemoryList
        memories={memories}
        onDelete={async (id) => {
          await memoryApi.remove(selectedProject, id)
          await refresh()
        }}
        onEdit={async (id, text) => {
          await memoryApi.update(selectedProject, id, text)
          await refresh()
        }}
      />
    </div>
  )
}
